import store from '@/store';
import { currencySuffix } from '../constants/currencySuffix';
import { currencyPrefix } from '../constants/currencyPrefix';

/**
 * Returns the currency suffix
 * @returns String
 */
const getCurrencySuffix = () => {
  const {
    env: { currency },
  } = store.getState().whitelabelConfig;
  let styledCurrency = currencySuffix[currency];
  const isEmptyString = typeof styledCurrency === 'string';
  if (!isEmptyString && !styledCurrency) {
    styledCurrency = currency || 'MXN';
  }
  return styledCurrency;
};

/**
 * Returns the currency prefix
 * @returns String
 */
const getCurrencyPrefix = () => {
  const {
    env: { currency },
  } = store.getState().whitelabelConfig;
  return currencyPrefix[currency] || '$';
};

/**
 * Returns the number of decimals for the currency
 * @returns Number
 */
export const getCurrencyDecimals = () => {
  const {
    env: { tripPriceDecimals },
  } = store.getState().whitelabelConfig;
  return tripPriceDecimals || 0;
};

/**
 * Get a number and return a readable quantity
 * @param {Number} quantity - Quantity to format
 * @returns Number
 */
const getReadableQuantity = (quantity) => {
  const replacesRegexComas = /\B(?=(\d{3})+(?!\d))/g;
  return quantity.toFixed(2).replace(replacesRegexComas, ',');
};

/**
 * Returns 0 if the number is negative, otherwise returns the number
 * @param {Number} number - The number to check
 * @returns {Number} - The original number or 0 if negative
 */
const negativeAsZero = (number) => (number < 0 ? 0 : number);

/**
 * Safely converts a price value to a number, handling various input types
 * @param {number|string} price - The price value to convert
 * @returns {number} - The numeric price value or 0 if invalid
 */
const safeParsePrice = (price) => {
  // If already a number, return it
  if (typeof price === 'number') {
    return Number.isNaN(price) ? 0 : price;
  }
  // If string, try to parse it
  if (typeof price === 'string') {
    // Normalize common thousand/decimal separators: "1.234,56" or "1,234.56"
    const normalized = price
      .trim()
      .replace(/\s/g, '') // remove all whitespace
      .replace(/[,](?=\d{3}\b)/g, '') // drop thousands commas
      .replace(/[.](?=\d{3}\b)/g, '') // drop thousands dots
      .replace(/,/, '.'); // convert decimal comma to dot (first occurrence)
    const parsed = Number.parseFloat(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  // Fallback to 0 for any other type
  return 0;
};

/**
 * Returns the number passed to the function formated as a currency
 *
 * @method formatCurrency
 * @param {Number} amount Number to format
 * @param {Number} decimals Number of decimal places
 * @param {String} currency Currency code (e.g., 'MXN', 'USD', 'BRL')
 * @param {String} locale Locale code (e.g., 'es-MX', 'pt-BR', 'en-US')
 * @return {String}
 */
export function formatCurrency(amount, decimals = 2, currency, locale) {
  const {
    features: { SHOW_CURRENCY_SUFFIX },
  } = store.getState().whitelabelConfig;
  const parsedAmount = safeParsePrice(amount);

  // Use Intl.NumberFormat when both currency and locale are provided
  if (currency && locale) {
    const formattedAmount = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(parsedAmount);
    return SHOW_CURRENCY_SUFFIX ? `${formattedAmount} ${currency}` : formattedAmount;
  }

  // Fallback to legacy formatting when currency or locale are not provided
  const formatted = parsedAmount.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const prefix = getCurrencyPrefix();
  const suffix = getCurrencySuffix();
  const formattedCurrency = SHOW_CURRENCY_SUFFIX
    ? `${prefix} ${formatted} ${suffix}`
    : `${prefix} ${formatted}`;
  return formattedCurrency;
}
/**
 * Format an insurance coverage amount using current currency configuration.
 * @param {number|string} amount - Amount to format.
 * @returns {string}
 */
const formatCoverageAmount = (amount) => {
  if (!amount && amount !== 0) return '';

  // Get currency and locale from whitelabel configuration
  const {
    env: { currency, locale },
  } = store.getState().whitelabelConfig;

  const decimals = getCurrencyDecimals();

  // Use the existing formatCurrency utility which properly handles
  // both string and number inputs via safeParsePrice
  return formatCurrency(amount, decimals, currency, locale);
};

export {
  getCurrencyPrefix,
  getCurrencySuffix,
  getReadableQuantity,
  negativeAsZero,
  safeParsePrice,
  formatCoverageAmount,
};
